import type { ServiceLineTreeNode } from "./serviceLineTreeTypes";
import { isCanonicalServiceLineAllNode } from "./serviceLineTreeUtils";

export function filterServiceLineRootsForSearch(
  roots: ServiceLineTreeNode[],
  query: string,
): ServiceLineTreeNode[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return roots;
  }
  return roots
    .map((root) => filterTreeNode(root, q))
    .filter((node): node is ServiceLineTreeNode => node !== null);
}

function filterTreeNode(
  node: ServiceLineTreeNode,
  q: string,
): ServiceLineTreeNode | null {
  const match = (n: ServiceLineTreeNode) => n.name.toLowerCase().includes(q);
  const children = node.children ?? [];

  if (isCanonicalServiceLineAllNode(node) && children.length === 0) {
    return match(node) ? node : null;
  }

  const filteredKids = children
    .map((c) => filterTreeNode(c, q))
    .filter((n): n is ServiceLineTreeNode => n !== null);

  if (match(node)) {
    return { ...node, children: [...children] };
  }
  if (filteredKids.length > 0) {
    return { ...node, children: filteredKids };
  }
  return null;
}

export function buildBranchOpenForFilteredSections(
  filteredRoots: ServiceLineTreeNode[],
  query: string,
  defaultExpandedRootSectionName: string = "service lines",
): Record<string, boolean> {
  const qTrim = query.trim().toLowerCase();
  const next: Record<string, boolean> = {};
  const defaultExpandedLower = defaultExpandedRootSectionName
    .trim()
    .toLowerCase();
  filteredRoots.forEach((section, i) => {
    const sectionKey = `section-${i}`;
    const hasSectionChildren = (section.children ?? []).length > 0;
    if (!hasSectionChildren) {
      return;
    }
    if (!qTrim) {
      next[sectionKey] =
        section.name.trim().toLowerCase() === defaultExpandedLower;
      return;
    }
    const sectionMatches = section.name.toLowerCase().includes(qTrim);
    next[sectionKey] = !sectionMatches;
  });
  return next;
}

export function buildNodeExpandedDefaultsForSearch(
  nodes: ServiceLineTreeNode[],
  depth: number,
  query: string,
  keyPrefix: string,
): Record<string, boolean> {
  const qTrim = query.trim().toLowerCase();
  const out: Record<string, boolean> = {};
  if (!qTrim) {
    return out;
  }
  const match = (n: ServiceLineTreeNode) =>
    n.name.toLowerCase().includes(qTrim);

  const walk = (list: ServiceLineTreeNode[], d: number) => {
    list.forEach((node, i) => {
      const children = node.children ?? [];
      if (children.length > 0) {
        const key = `${keyPrefix}${d}-${i}-${node.name}`;
        out[key] = !match(node);
        walk(children, d + 1);
      }
    });
  };
  walk(nodes, depth);
  return out;
}

function findAncestorKeysToSelected(
  nodes: ServiceLineTreeNode[],
  selectedId: string,
  depth: number,
  treeKeyPrefix: string,
  ancestors: string[],
): string[] | null {
  for (let nodeIndex = 0; nodeIndex < nodes.length; nodeIndex++) {
    const node = nodes[nodeIndex];
    const children = node.children ?? [];
    const rowKey = `${treeKeyPrefix}${depth}-${nodeIndex}-${node.name}`;
    if (
      node.space_organization_unit_id !== null &&
      String(node.space_organization_unit_id) === selectedId
    ) {
      return ancestors;
    }
    if (children.length === 0) {
      continue;
    }
    const found = findAncestorKeysToSelected(
      children,
      selectedId,
      depth + 1,
      treeKeyPrefix,
      [...ancestors, rowKey],
    );
    if (found) {
      return found;
    }
  }
  return null;
}

export function buildExpansionStateForSelection(
  roots: ServiceLineTreeNode[],
  selectedId: string,
  defaultExpandedRootSectionName: string = "service lines",
): {
  branchOpen: Record<string, boolean>;
  nodeExpanded: Record<string, boolean>;
} {
  const branchOpen = buildBranchOpenForFilteredSections(
    roots,
    "",
    defaultExpandedRootSectionName,
  );
  const nodeExpanded: Record<string, boolean> = {};

  if (!selectedId) {
    return { branchOpen, nodeExpanded };
  }

  for (let sectionIndex = 0; sectionIndex < roots.length; sectionIndex++) {
    const section = roots[sectionIndex];
    const sectionChildren = section.children ?? [];
    const sectionKey = `section-${sectionIndex}`;

    if (
      section.space_organization_unit_id !== null &&
      String(section.space_organization_unit_id) === selectedId
    ) {
      if (sectionChildren.length > 0) {
        branchOpen[sectionKey] = true;
      }
      return { branchOpen, nodeExpanded };
    }

    if (sectionChildren.length === 0) {
      continue;
    }

    const ancestorKeys = findAncestorKeysToSelected(
      sectionChildren,
      selectedId,
      0,
      `${sectionIndex}-`,
      [],
    );
    if (!ancestorKeys) {
      continue;
    }

    branchOpen[sectionKey] = true;
    for (const key of ancestorKeys) {
      nodeExpanded[key] = true;
    }
    return { branchOpen, nodeExpanded };
  }

  return { branchOpen, nodeExpanded };
}
